import type { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { Breadcrumb } from 'components/Breadcrumb/Breadcrumb';
import { GradientOverflow } from 'components/GradientOverflow/GradientOverflow';
import { SearchModal } from 'components/pages/SchemasV2/SearchModal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';

import { ToastProvider } from '../../wrappers/ToastProvider';
import { DetailsButton } from './DetailsButton';

type MainViewLayoutProps = {
  detailSideBar?: React.ReactNode;
  onDetailSideBarClose?: () => void;
  subHeader?: React.ReactNode;
  summarySection?: React.ReactNode;
  summarySectionActions?: React.ReactNode;
  hideSearch?: boolean;
  'data-testid'?: string;
  hideGradientOverflow?: boolean;
  detailsContent?: React.ReactNode;
  mode?: 'payments';
};

export const MainViewLayout = ({
  children,
  summarySection,
  summarySectionActions,
  hideSearch = false,
  subHeader,
  detailSideBar,
  onDetailSideBarClose,
  'data-testid': dataTestId,
  hideGradientOverflow = false,
  detailsContent,
  mode,
}: PropsWithChildren<MainViewLayoutProps>) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [detailsSideBarVisible, setDetailsSideBarVisible] = useState(false);

  const onClick = useCallback(() => {
    if (detailSideBar) {
      onDetailSideBarClose?.();
    }
    setDetailsSideBarVisible(false);
  }, [detailSideBar, onDetailSideBarClose]);

  // close sidebar with esc
  useHotkeys('esc', onClick);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    body.addEventListener('click', onClick);
    // eslint-disable-next-line consistent-return
    return () => body.removeEventListener('click', onClick);
  }, [detailSideBar, onClick]);

  // search bar
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  useHotkeys(
    'mod+k',
    (e) => {
      if (!hideSearch) {
        setSearchModalVisible(true);
        e.preventDefault();
      }
    },
    [hideSearch, setSearchModalVisible]
  );

  // details sidebar
  const handleDetailsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setDetailsSideBarVisible(!detailsSideBarVisible);
    },
    [detailsSideBarVisible]
  );

  // reposition subheader when detailSideBar is open
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const checkFullScreen = () => {
      // use a 254 px fudge factor to account for left nav
      setIsFullScreen(
        body.getBoundingClientRect().width + 254 >= window.innerWidth
      );
    };

    checkFullScreen();

    const resizeObserver = new ResizeObserver(checkFullScreen);
    resizeObserver.observe(body);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <ToastProvider>
      {/* 100dvh - 54px comes from using the full page height minus the height of the header */}
      <div
        className={clsx([
          'h-full max-h-[100dvh] overscroll-none relative w-full flex flex-row text-md overflow-x-hidden',
        ])}
      >
        <div
          className={clsx([
            'flex flex-col h-full w-full overflow-x-hidden overflow-y-hidden',
            mode !== 'payments' &&
              'pl-f1 laptop:pl-f2 pt-f1 laptop:pt-f2 space-y-f4',
          ])}
          data-testid={dataTestId}
          ref={bodyRef}
        >
          <div
            data-testid="main-view-top-bar"
            className={clsx(
              'flex flex-row justify-between w-full gap-y-f2 flex-wrap h-fit',
              mode === 'payments' &&
                !!subHeader &&
                'border-b-[0.5px] border-main-300'
            )}
          >
            {/* subheader or breadcrumb in left column */}
            {subHeader ? (
              <div
                data-testid="subHeader"
                className={clsx([
                  'w-full h-min ',
                  isFullScreen ? 'pl-f4' : 'pl-f0',
                ])}
              >
                {subHeader}
              </div>
            ) : (
              <div
                data-testid="breadcrumb"
                className={clsx([
                  'min-h-f2 w-fit min-w-fit wideTablet:overflow-visible',
                  isFullScreen ? 'pl-f3 laptop:pl-f4' : 'pl-f0',
                ])}
              >
                <Breadcrumb />
              </div>
            )}
            {/* Search and Action Buttons in right column */}
            <div
              className={clsx('flex flex-row w-fit h-f2 justify-end gap-x-f1')}
            >
              {summarySectionActions}
              {detailsContent && <DetailsButton onClick={handleDetailsClick} />}
              {!hideSearch && (
                <button
                  type="button"
                  className="hover:bg-negative bg-main-200 text-start h-f2 min-w-f2 flex items-center justify-center"
                  onClick={() => setSearchModalVisible(true)}
                  aria-label="Search"
                  data-testid="search-button"
                >
                  <span>
                    <Icon type="search" size="md" />
                  </span>
                </button>
              )}
            </div>
          </div>
          <div
            data-testid="main-view-main"
            className="flex flex-col w-full h-full overflow-y-scroll scrollbar-hide gap-y-f4"
          >
            {summarySection && (
              <div
                data-testid="summarySection"
                className="grid grid-cols-1 laptop:grid-cols-2 laptop:gap-x-f2  w-full justify-items-end h-min z-10"
              >
                <div className={clsx(['w-full'])}>{summarySection}</div>
              </div>
            )}
            <div
              data-testid="main-view-content"
              className={clsx([
                'h-full w-full flex-col max-h-full',
                mode === 'payments' && 'pl-f1 laptop:pl-f2',
              ])}
            >
              {children}
            </div>
            {!hideGradientOverflow && <GradientOverflow bodyRef={bodyRef} />}
          </div>
        </div>
        {detailSideBar && (
          <div
            data-testid="layout-right-section"
            className={clsx(
              'absolute right-0 bg-canvas h-full items-start min-w-f21 w-f21 max-w-f21 border-l-[0.5px] border-main-300 ml-f2 pl-f2 z-30',
              'pt-f2'
            )}
          >
            {detailSideBar}
          </div>
        )}
        {detailsContent && detailsSideBarVisible && (
          <div
            data-testid="layout-details-section"
            className={clsx(
              'absolute right-0 bg-canvas h-full items-start min-w-f21 w-f21 max-w-f21 border-l-[0.5px] border-main-300 ml-f2 pl-f2 z-30',
              'pt-f2'
            )}
          >
            {detailsContent}
          </div>
        )}
        {searchModalVisible && (
          <SearchModal
            onNavigate={() => setSearchModalVisible(false)}
            onClose={() => setSearchModalVisible(false)}
          />
        )}
      </div>
    </ToastProvider>
  );
};
